import React, { useState } from 'react';
import { Card, Table, Button, Space, Typography, Tag, Modal, Spin } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, TagsOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useProductAttributes } from '../hooks/useProductAttributes';
import type { ProductAttribute } from '../services/ProductAttributeService';
import { ProductAttributeFormModal } from '../components/ProductAttributeFormModal';

const { Title, Text } = Typography;

export const ProductAttributesScreen: React.FC = () => {
  const { attributes, loading, error, deleteAttribute, createAttribute, updateAttribute } = useProductAttributes();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState<ProductAttribute | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleEdit = (record: ProductAttribute) => {
    setEditingAttribute(record);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setEditingAttribute(null);
    setIsModalOpen(true);
  };

  const handleSubmit = async (values: Partial<ProductAttribute>) => {
    try {
      setIsSubmitting(true);
      if (editingAttribute) {
        await updateAttribute(editingAttribute.id, values);
      } else {
        await createAttribute(values);
      }
      setIsModalOpen(false);
    } catch (err) {
      console.error('Failed to save attribute:', err);
      // Let modal stay open to correct errors
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (text: string) => <Tag color="default" className="font-mono">{text}</Tag>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: 'Purpose',
      dataIndex: 'purpose_id',
      key: 'purpose_id',
      render: (purposeId: number) => {
        if (purposeId === 1) return <Text type="secondary">Specification</Text>;
        if (purposeId === 2) return <Text type="secondary">Quotation</Text>;
        return <Text type="secondary">Both</Text>;
      },
    },
    {
      title: 'Values',
      key: 'values',
      render: (_: any, record: ProductAttribute) => (
        <Space>
          <UnorderedListOutlined style={{ color: '#8c8c8c' }} />
          <Text type="secondary">{record.values?.length || 0} preset values</Text>
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right' as const,
      render: (_: any, record: ProductAttribute) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Button 
            danger 
            icon={<DeleteOutlined />} 
            onClick={() => {
              Modal.confirm({
                title: 'Delete Attribute',
                content: `Are you sure you want to delete ${record.name}?`,
                okText: 'Yes',
                okType: 'danger',
                cancelText: 'No',
                onOk: () => deleteAttribute(record.id),
              });
            }} 
          />
        </Space>
      ),
    },
  ];

  if (error) return <div className="p-8 text-center text-red-500">{error}</div>;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>Product Attributes</Title>
          <Text type="secondary">Manage product-specific attributes and specifications</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          New Attribute
        </Button>
      </div>

      <Card styles={{ body: { padding: 0 } }}>
        <Table
          dataSource={attributes}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: (
            <div style={{ padding: '32px 0', textAlign: 'center' }}>
              <TagsOutlined style={{ fontSize: 32, color: '#d9d9d9', marginBottom: 12 }} />
              <div><Text type="secondary">No attributes configured yet.</Text></div>
            </div>
          ) }}
        />
      </Card>

      <ProductAttributeFormModal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        initialValues={editingAttribute}
        isLoading={isSubmitting}
      />
    </Space>
  );
};
